import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import {
  WhatsappLogo,
  Lightning,
  ChartLineUp,
  Users,
  Moon,
  Sun,
  SignOut,
  UserCircle,
  PlusCircle,
  ClipboardText,
  Robot,
  Trophy,
  FileText,
  Hash,
  Megaphone,
  Database,
  HandWaving,
  GooglePlayLogo,
  SquaresFour,
  CaretDown,
  CheckCircle,
  Receipt,
  PhoneSlash,
  PhoneCall,
  Prohibit,
  Pulse,
} from "@phosphor-icons/react";
import OnlineChip from "@/components/hub/OnlineChip";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import InstallPWAButton from "@/components/InstallPWAButton";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";

export default function StatusBar({
  source,
  lastUpdate,
  onGoHome,
  onInjectTest,
  onOpenStats,
  onOpenTeam,
  onOpenBookings,
  onOpenAgentStats,
  onOpenTemplates,
  onOpenShortcuts,
  onOpenCampaigns,
  onOpenBots,
  onOpenDbStats,
  onOpenWelcome,
  onOpenPlayReviews,
  onNewChat,
}) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const live = source === "sse";
  const connecting = source === "connecting" || source === "reconnecting";
  const dotColor = live ? "bg-[#10B981]" : connecting ? "bg-[#F59E0B]" : "bg-[#EF4444]";
  const statusText = live ? "Realtime · SSE" : connecting ? "Connecting…" : "Offline";

  const [botGlobal, setBotGlobal] = useState(true);
  const [botFlows, setBotFlows] = useState({ booking: true, status: true, complaint: true, rodbez: true });
  const [saving, setSaving] = useState(false);

  const refreshFlags = () => {
    api
      .get("/bot/global")
      .then((r) => {
        setBotGlobal(!!r.data.enabled);
        if (r.data.flows) setBotFlows(r.data.flows);
      })
      .catch(() => {});
  };
  useEffect(() => {
    refreshFlags();
    // Re-fetch every 30s and whenever the tab regains focus, so any toggle
    // made in another browser tab / device is reflected here within ~30s
    // (or instantly on tab switch).
    const interval = setInterval(refreshFlags, 30000);
    const onFocus = () => refreshFlags();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFlags = async (payload) => {
    setSaving(true);
    try {
      const r = await api.post("/bot/global", payload);
      setBotGlobal(!!r.data.enabled);
      if (r.data.flows) setBotFlows(r.data.flows);
    } catch (_) {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  const toggleBotGlobal = () => updateFlags({ enabled: !botGlobal });
  const toggleFlow = (key) =>
    updateFlags({ flows: { ...botFlows, [key]: !botFlows[key] } });

  const activeFlows = Object.values(botFlows).filter(Boolean).length;
  const totalFlows = Object.keys(botFlows).length;
  const botSummary = !botGlobal
    ? "OFF"
    : activeFlows === totalFlows
    ? "ON"
    : `${activeFlows}/${totalFlows}`;

  return (
    <header
      className="flex items-center justify-between px-2 md:px-5 h-14 border-b border-black/60 bg-[#0B0F14] text-white gap-1.5 md:gap-2"
      data-testid="status-bar"
    >
      <button
        type="button"
        onClick={onGoHome}
        className="flex items-center gap-2 md:gap-3 min-w-0 hover:opacity-80 transition-opacity rounded-md -ml-1 px-1 focus:outline-none focus:ring-2 focus:ring-[#25D366]/50"
        data-testid="statusbar-home-btn"
        title="Go to home — close any open chat"
      >
        <div className="h-8 w-8 rounded-md bg-[#25D366] grid place-items-center text-white shrink-0">
          <WhatsappLogo weight="fill" size={18} />
        </div>
        <div className="min-w-0 text-left">
          <div className="heading font-semibold text-[13px] md:text-[15px] leading-tight truncate text-white">
            RodBez Whatsapp
          </div>
          <div className="hidden md:block text-[11px] md:text-xs text-white/60 leading-tight truncate">
            {user?.full_name || user?.email} · {user?.role}
          </div>
        </div>
      </button>

      <div className="flex items-center gap-1 md:gap-2 shrink-0">
        <div
          className="hidden lg:flex items-center gap-2 px-3 h-8 rounded-full border border-white/15 bg-white/5 text-white/80"
          data-testid="webhook-status"
        >
          <span className={`h-2 w-2 rounded-full ${dotColor} ${live ? "live-dot" : ""}`} />
          <span className="text-xs font-medium tracking-wide">{statusText}</span>
          {lastUpdate && (
            <span className="text-[11px] text-white/50">
              · {formatDistanceToNow(new Date(lastUpdate), { addSuffix: true })}
            </span>
          )}
        </div>

        <OnlineChip currentUserId={user?.id} />

        <InstallPWAButton />

        <Button
          variant="default"
          size="sm"
          className="h-8 gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-white px-2 md:px-3"
          onClick={onNewChat}
          data-testid="new-chat-btn"
          title="New chat"
        >
          <PlusCircle size={14} weight="fill" /> <span className="hidden md:inline">New chat</span>
        </Button>

        {/* Bot-settings pill — compact status + click to tune flows (hidden on mobile; available via Manage menu) */}
        {user?.role === "admin" && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`hidden md:inline-flex h-8 gap-1.5 px-2 md:px-3 bg-white/5 hover:bg-white/10 ${
                  botGlobal
                    ? activeFlows === totalFlows
                      ? "border-[#6EE7B7]/70 text-[#6EE7B7] hover:text-[#6EE7B7]"
                      : "border-[#FCD34D]/70 text-[#FCD34D] hover:text-[#FCD34D]"
                    : "border-[#FCA5A5]/70 text-[#FCA5A5] hover:text-[#FCA5A5]"
                }`}
                data-testid="bot-settings-btn"
                title="Bot settings"
              >
                <Robot size={14} weight="fill" />
                <span className="hidden md:inline">Bot {botSummary}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72" data-testid="bot-settings-popover">
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-semibold heading">Bot Settings</div>
                  <div className="text-[11px] text-[var(--muted)]">
                    Control the chatbot & individual flows
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-md border border-[var(--border)] p-2.5">
                  <div>
                    <div className="text-sm font-medium">Global Bot</div>
                    <div className="text-[11px] text-[var(--muted)]">
                      Master switch for the entire bot
                    </div>
                  </div>
                  <Switch
                    checked={botGlobal}
                    onCheckedChange={toggleBotGlobal}
                    disabled={saving}
                    data-testid="bot-global-switch"
                  />
                </div>
                <div className="space-y-2 pt-1">
                  <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] font-semibold">
                    Flows
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm flex items-center gap-1.5">
                        🚖 RodBez Booking
                        <button
                          type="button"
                          onClick={() => window.open("/rodbez-flow", "_blank", "noopener")}
                          className="text-[10px] uppercase tracking-wide font-semibold text-[#0EA5E9] hover:underline"
                          data-testid="rodbez-flow-view-btn"
                          title="Preview the bot's full flow & messages"
                        >
                          view flow ↗
                        </button>
                      </div>
                      <div className="text-[11px] text-[var(--muted)]">
                        Auto-greet + cab booking via RodBez API
                      </div>
                    </div>
                    <Switch
                      checked={botFlows.rodbez}
                      onCheckedChange={() => toggleFlow("rodbez")}
                      disabled={saving || !botGlobal}
                      data-testid="bot-flow-rodbez-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm">🚕 New Booking</div>
                      <div className="text-[11px] text-[var(--muted)]">
                        Cab booking conversation
                      </div>
                    </div>
                    <Switch
                      checked={botFlows.booking}
                      onCheckedChange={() => toggleFlow("booking")}
                      disabled={saving || !botGlobal}
                      data-testid="bot-flow-booking-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm">📍 Booking Status</div>
                      <div className="text-[11px] text-[var(--muted)]">
                        Lookup by booking ID
                      </div>
                    </div>
                    <Switch
                      checked={botFlows.status}
                      onCheckedChange={() => toggleFlow("status")}
                      disabled={saving || !botGlobal}
                      data-testid="bot-flow-status-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm">🛠️ Complaint</div>
                      <div className="text-[11px] text-[var(--muted)]">
                        Register a complaint
                      </div>
                    </div>
                    <Switch
                      checked={botFlows.complaint}
                      onCheckedChange={() => toggleFlow("complaint")}
                      disabled={saving || !botGlobal}
                      data-testid="bot-flow-complaint-switch"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-[var(--muted)] pt-1 border-t border-[var(--border)]">
                  Disabled flows show a polite "temporarily unavailable" message.
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Divider */}
        <div className="hidden md:block h-5 w-px bg-white/15 mx-0.5" />

        {/* Reply-time Quick actions: Bookings, Templates, Shortcuts (hidden on mobile) */}
        <Button
          variant="ghost"
          size="sm"
          className="hidden md:inline-flex h-8 gap-1.5 px-2 text-white/85 hover:bg-white/10 hover:text-white"
          onClick={() => window.open("/?view=bookings", "_blank", "noopener")}
          data-testid="bookings-btn"
          title="Bookings (opens in new tab)"
        >
          <ClipboardText size={14} weight="fill" />
          <span className="hidden lg:inline text-xs">Bookings</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="hidden md:inline-flex h-8 gap-1.5 px-2 text-white/85 hover:bg-white/10 hover:text-white"
          onClick={onOpenTemplates}
          data-testid="templates-btn"
          title="WhatsApp Templates"
        >
          <FileText size={14} weight="fill" className="text-[#60A5FA]" />
          <span className="hidden lg:inline text-xs">Templates</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="hidden md:inline-flex h-8 gap-1.5 px-2 text-white/85 hover:bg-white/10 hover:text-white"
          onClick={onOpenShortcuts}
          data-testid="shortcuts-btn"
          title="Quick Shortcuts (#code)"
        >
          <Hash size={14} weight="bold" className="text-[#25D366]" />
          <span className="hidden lg:inline text-xs">Shortcuts</span>
        </Button>

        {/* Divider */}
        <div className="hidden md:block h-5 w-px bg-white/15 mx-0.5" />

        {/* Manage dropdown — Stats, Campaigns, Bots, Agents, Team, DB */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
              data-testid="manage-menu-btn"
              title="Manage"
            >
              <SquaresFour size={14} weight="bold" />
              <span className="hidden md:inline text-xs">Manage</span>
              <CaretDown size={10} weight="bold" className="opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="manage-menu-content">
            {/* Mobile-only quick actions (hidden on desktop because they're already in the top bar) */}
            <div className="md:hidden">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-[var(--muted)] font-semibold">
                Quick actions
              </div>
              <DropdownMenuItem
                onClick={() => window.open("/?view=bookings", "_blank", "noopener")}
                data-testid="bookings-btn-mobile"
              >
                <ClipboardText size={14} className="mr-2" /> Bookings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open("/customers", "_blank", "noopener")}
                data-testid="customers-btn"
              >
                <Users size={14} className="mr-2 text-[#25D366]" weight="fill" /> Customer Directory
                <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenTemplates} data-testid="templates-btn-mobile">
                <FileText size={14} className="mr-2 text-[#60A5FA]" /> Templates
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenShortcuts} data-testid="shortcuts-btn-mobile">
                <Hash size={14} className="mr-2 text-[#25D366]" /> Shortcuts
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </div>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-[var(--muted)] font-semibold">
              Analytics
            </div>
            <DropdownMenuItem onClick={onOpenStats} data-testid="stats-btn">
              <ChartLineUp size={14} className="mr-2" /> Inbox stats
            </DropdownMenuItem>
            {onOpenPlayReviews && (
              <DropdownMenuItem onClick={onOpenPlayReviews} data-testid="play-reviews-btn">
                <GooglePlayLogo size={14} className="mr-2 text-[#34A853]" weight="fill" /> Play Store reviews
              </DropdownMenuItem>
            )}
            {user?.role === "admin" && (
              <DropdownMenuItem onClick={onOpenAgentStats} data-testid="agent-stats-btn">
                <Trophy size={14} className="mr-2 text-[#FBBF24]" /> Agent performance
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => window.open("/reports", "_blank", "noopener")}
              data-testid="reports-page-btn"
            >
              <Trophy size={14} className="mr-2 text-[#FBBF24]" /> Reports
              <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => window.open("/invoice", "_blank", "noopener")}
              data-testid="invoice-page-btn"
            >
              <Receipt size={14} className="mr-2 text-[#10B981]" weight="duotone" /> Invoice preview
              <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
            </DropdownMenuItem>
            {user?.role === "admin" && (
              <DropdownMenuItem
                onClick={() => window.open("/ad-performance", "_blank", "noopener")}
                data-testid="ad-performance-btn"
              >
                <ChartLineUp size={14} className="mr-2 text-[#1877F2]" /> Ad performance
                <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
              </DropdownMenuItem>
            )}
            {user?.role === "admin" && (
              <DropdownMenuItem
                onClick={() => window.open("/missed-calls", "_blank", "noopener")}
                data-testid="missed-calls-btn"
              >
                <PhoneSlash size={14} className="mr-2 text-[#F43F5E]" weight="duotone" /> Missed calls
                <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
              </DropdownMenuItem>
            )}
            {/* Per user rule 2026-02-14 — Voice test entry hidden for
                now. Re-enable when Tata Voice Bot + DTMF setup is ready. */}
            {/*
            {user?.role === "admin" && (
              <DropdownMenuItem
                onClick={() => window.open("/voice-test", "_blank", "noopener")}
                data-testid="voice-test-btn"
              >
                <PhoneCall size={14} className="mr-2 text-emerald-400" weight="duotone" /> Voice test call
                <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
              </DropdownMenuItem>
            )}
            */}
            {user?.role === "admin" && (
              <DropdownMenuItem
                onClick={() => window.open("/bot-health", "_blank", "noopener")}
                data-testid="bot-health-btn"
              >
                <Pulse size={14} className="mr-2 text-[#10B981]" weight="bold" /> Bot health
                <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
              </DropdownMenuItem>
            )}
            {user?.role === "admin" && (
              <DropdownMenuItem
                onClick={() => window.open("/bot-management", "_blank", "noopener")}
                data-testid="bot-management-btn"
              >
                <Robot size={14} className="mr-2 text-[#6366F1]" weight="fill" /> Bot management
                <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
              </DropdownMenuItem>
            )}
            {user?.role === "admin" && (
              <DropdownMenuItem
                onClick={() => window.open("/blocked-customers", "_blank", "noopener")}
                data-testid="blocked-customers-btn"
              >
                <Prohibit size={14} className="mr-2 text-[#F43F5E]" weight="fill" /> Blocked customers
                <span className="ml-auto text-[9px] text-[var(--muted)]">↗</span>
              </DropdownMenuItem>
            )}
            {user?.role === "admin" && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-[var(--muted)] font-semibold">
                  Automation
                </div>
                <DropdownMenuItem onClick={onOpenCampaigns} data-testid="campaigns-btn">
                  <Megaphone size={14} className="mr-2 text-[#34D399]" /> Campaigns
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onOpenBots} data-testid="bots-btn">
                  <Robot size={14} className="mr-2 text-[#A78BFA]" /> Bot builder
                </DropdownMenuItem>
                {onOpenWelcome && (
                  <DropdownMenuItem onClick={onOpenWelcome} data-testid="welcome-messages-btn">
                    <HandWaving size={14} className="mr-2 text-[#EA580C]" weight="fill" /> Welcome messages
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-[var(--muted)] font-semibold">
                  Admin
                </div>
                <DropdownMenuItem onClick={onOpenTeam} data-testid="team-btn">
                  <Users size={14} className="mr-2" /> Team members
                </DropdownMenuItem>
                {onOpenDbStats && (
                  <DropdownMenuItem onClick={onOpenDbStats} data-testid="db-stats-btn-manage">
                    <Database size={14} className="mr-2" /> Database storage
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={async () => {
                    const t = toast.loading("Firing Meta App Review test calls…");
                    try {
                      const r = await api.post("/meta-review/fire-test-calls");
                      const d = r.data || {};
                      const totalOk = Object.values(d).reduce((a, v) => a + (v.ok || 0), 0);
                      const totalAll = Object.values(d).reduce((a, v) => a + (v.total || 0), 0);
                      toast.success(
                        `${totalOk}/${totalAll} calls OK · Wait 24h for Meta to update green ticks`,
                        { id: t, duration: 8000 },
                      );
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Failed to fire test calls", { id: t });
                    }
                  }}
                  data-testid="meta-review-fire-btn"
                >
                  <CheckCircle size={14} className="mr-2 text-[#1877F2]" weight="fill" /> Meta App Review · Fire test calls
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="outline"
          size="sm"
          className="hidden sm:inline-flex h-8 gap-1.5 px-2 md:px-3 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
          onClick={onInjectTest}
          data-testid="inject-test-btn"
          title="Inject test message"
        >
          <Lightning size={14} weight="fill" /> <span className="hidden md:inline">Test</span>
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-white hover:bg-white/10 hover:text-white" onClick={toggle} data-testid="theme-toggle">
          {theme === "dark" ? <Sun size={16} weight="fill" /> : <Moon size={16} weight="fill" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-white hover:bg-white/10 hover:text-white" data-testid="user-menu-btn">
              <UserCircle size={20} weight="fill" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5 text-xs">
              <div className="font-medium">{user?.full_name}</div>
              <div className="text-[var(--muted)]">{user?.email}</div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onInjectTest} className="sm:hidden" data-testid="logout-inject-test">
              <Lightning size={14} className="mr-2" /> Inject test
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="logout-btn">
              <SignOut size={14} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
